import React, { useEffect, useRef, useState, useCallback } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

interface TeamMember {
  image: string;
  name: string;
  position: string;
  description: string;
  linkedinUrl?: string;
}

const Team: React.FC = () => {
  const [currentMember, setCurrentMember] = useState(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const photoRef = useRef<HTMLDivElement>(null);
  const infoRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const linkedinRef = useRef<HTMLAnchorElement>(null);
  const progressRef = useRef<SVGPathElement>(null);

  const team: TeamMember[] = [
    {
      image: "/Adobe Express - file (2) (1).avif",
      name: "Sandeep Singh",
      position: "Director",
      description: "As a visionary leader, Sandeep has been instrumental in driving innovation and developing strategic growth plans for the Group. Recognized as one of India's most promising entrepreneurs by ET Edge, a testament to his innovative spirit and leadership expertise. He is driving group's future innovations strategies.",
      linkedinUrl: "https://www.linkedin.com/in/sandeep-singh-8941a216/"
    },
    {
      image: "/Adobe Express - file (1).avif",
      name: "Arunima Singh",
      position: "Director",
      description: "Arunima is a seasoned leader who spearheads top-tier leadership teams to deliver exceptional engineering services and drive the development of innovative electric vehicles (EVs). With a keen eye for talent, she nurtures and develops high-performing teams across group companies. Domain: Finance, Engineering Services, HRD & high-performance team building, Business & growth Strategies, Start-ups, Electric vehicle venture ~Dawki Mobility",
      linkedinUrl: "https://www.linkedin.com/in/arunima-singh-25271713/"
    },
    {
      image: "/Remove background project (2) copy.avif",
      name: "Abhay Abhyankar",
      position: "Chief Executive Officer",
      description: "Abhay's expertise in production process, workflow management, cost control, and SCM has been instrumental in driving success. His leadership skills and commitment to continuous innovation have enabled the company to achieve product benchmarking, setting new standards for excellence. 35yrs of Exp ~Kinetic Green, Force Motors, Kirloskar Oil Engines, Tata Ficosa and Remsons Industry, Domain: Production Process, Innovation, Start-ups, new Technologies",
      linkedinUrl: "https://www.linkedin.com/in/abhay-abhyankar-9ba09811/"
    },
    {
      image: "/Adobe Express - file (3).avif",
      name: "Purnendra Bhattacharjee",
      position: "National Head – After Sales",
      description: "With over 18 years in the automotive industry, Purnendra leads Dawki Mobility's After Sales division. He brings extensive experience from Force Motors, Piaggio India, and Mahindra, with a specialization in customer service, channel development, and team leadership. Known for improving service quality and customer satisfaction, he plays a key role in enhancing the overall ownership experience.",
      linkedinUrl: "https://www.linkedin.com/in/purnendra-bhattacharjee-8238061b/"
    }
  ];

  // Check for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Initial animations
  useEffect(() => {
    const section = sectionRef.current;
    const title = titleRef.current;
    const subtitle = subtitleRef.current;
    const photo = photoRef.current;
    const info = infoRef.current;

    if (!section || !title || !subtitle || !photo || !info) return;

    // Initial states
    gsap.set([title, subtitle], {
      opacity: 0,
      y: 50
    });

    gsap.set(photo, {
      opacity: 0,
      scale: 0.8,
      rotationY: -15
    });

    gsap.set(info, {
      opacity: 0,
      x: 50
    });

    // Scroll-triggered animations
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: "top 80%",
        end: "bottom 20%",
        toggleActions: "play none none reverse"
      }
    });

    tl.to(title, {
      opacity: 1,
      y: 0,
      duration: 0.8,
      ease: "back.out(1.7)"
    })
      .to(subtitle, {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: "power2.out"
      }, "-=0.4")
      .to(photo, {
        opacity: 1,
        scale: 1,
        rotationY: 0,
        duration: 1,
        ease: "back.out(1.7)"
      }, "-=0.3")
      .to(info, {
        opacity: 1,
        x: 0,
        duration: 0.8,
        ease: "power2.out"
      }, "-=0.5");

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  // Auto-scroll functionality with GSAP animations
  const nextMember = useCallback(() => {
    if (prefersReducedMotion) return;

    const photo = photoRef.current;
    const info = infoRef.current;
    const linkedin = linkedinRef.current;
    const progress = progressRef.current;

    if (!photo || !info) return;

    // Animate out current member
    const tl = gsap.timeline();

    tl.to([photo, info], {
      opacity: 0,
      duration: 0.3,
      ease: "power2.in"
    })
      .to(photo, {
        scale: 0.95,
        rotationY: -5,
        duration: 0.3,
        ease: "power2.in"
      }, 0)
      .to(info, {
        x: -30,
        duration: 0.3,
        ease: "power2.in"
      }, 0)
      .call(() => {
        setCurrentMember((prev) => (prev + 1) % team.length);
      })
      .to([photo, info], {
        opacity: 1,
        duration: 0.5,
        ease: "power2.out"
      })
      .to(photo, {
        scale: 1,
        rotationY: 0,
        duration: 0.5,
        ease: "back.out(1.7)"
      }, "-=0.5")
      .to(info, {
        x: 0,
        duration: 0.5,
        ease: "power2.out"
      }, "-=0.5");

    // Animate progress ring
    if (progress) {
      gsap.to(progress, {
        strokeDashoffset: 0,
        duration: 5,
        ease: "none"
      });
    }
  }, [prefersReducedMotion, team.length]);

  // Auto-scroll timer
  useEffect(() => {
    if (!prefersReducedMotion) {
      intervalRef.current = setInterval(nextMember, 5000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [prefersReducedMotion, nextMember]);

  const currentTeamMember = team[currentMember];

  return (
    <section
      ref={sectionRef}
      id="team-section"
      className="py-20 bg-gradient-to-b from-white via-[#F8F9FA] to-white overflow-hidden"
      aria-label="Leadership team profiles"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 ref={titleRef} className="text-4xl md:text-5xl font-bold text-[#1A1A1A] mb-6">
            Meet Our Leadership
          </h2>
          <p ref={subtitleRef} className="text-xl text-gray-600 max-w-3xl mx-auto">
            Our diverse team of experts brings together decades of experience in automotive innovation,
            design, and sustainable technology to shape the future of mobility.
          </p>
        </div>

        {/* Team Member Display */}
        <div className="relative max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[600px]">
            {/* Member Photo */}
            <div className="relative">
              <div
                ref={photoRef}
                className="relative overflow-hidden rounded-2xl shadow-2xl transform-gpu"
                style={{ perspective: "1000px" }}
              >
                <img
                  src={currentTeamMember.image}
                  alt={`${currentTeamMember.name}, ${currentTeamMember.position}`}
                  className="w-full h-[500px] object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>

                {/* LinkedIn Link */}
                <div className="absolute top-5 right-5 z-10">
                  <a
                    href={currentTeamMember.linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-0.5 rounded transition-all duration-300 hover:scale-110 group"
                    aria-label="Connect on LinkedIn"
                    title="Visit LinkedIn profile"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="28"
                      height="28"
                      viewBox="0 0 34 34"
                      fill="none"
                    >
                      <g>
                        <rect width="34" height="34" rx="6" fill="#0077B5" />
                        <path d="M10.1 13.5h3V24h-3V13.5zm1.5-4.2c1 0 1.7.7 1.7 1.6 0 .9-.7 1.6-1.7 1.6h-.1c-1 0-1.7-.7-1.7-1.6 0-.9.7-1.6 1.7-1.6zm4.7 4.2h2.9v1.4h.1c.4-.7 1.3-1.6 2.8-1.6 3 0 3.6 2 3.6 4.6V24h-3v-4.7c0-1.1 0-2.5-1.6-2.5-1.6 0-1.8 1.2-1.8 2.4V24h-3V13.5z" fill="white" />
                      </g>
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            {/* Member Info */}
            <div ref={infoRef} className="space-y-6">
              <div>
                <h3 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-2">
                  {currentTeamMember.name}
                </h3>
                <div className="text-xl md:text-2xl text-[#CC0000] font-semibold mb-6">
                  {currentTeamMember.position}
                </div>
              </div>

              <p className="text-lg text-gray-600 leading-relaxed">
                {currentTeamMember.description}
              </p>

              {/* Member Navigation Dots */}
              <div className="flex space-x-3 pt-6" role="tablist" aria-label="Team member navigation">
                {team.map((member, index) => (
                  <div
                    key={index}
                    className={`w-3 h-3 rounded-full transition-all duration-500 cursor-pointer ${index === currentMember
                        ? 'bg-[#CC0000] scale-125'
                        : 'bg-gray-300'
                      }`}
                    role="tab"
                    aria-selected={index === currentMember}
                    aria-label={`${member.name}'s profile indicator`}
                    onClick={() => {
                      if (index !== currentMember) {
                        setCurrentMember(index);
                        // Reset progress animation
                        if (progressRef.current) {
                          gsap.set(progressRef.current, { strokeDashoffset: 100 });
                          gsap.to(progressRef.current, {
                            strokeDashoffset: 0,
                            duration: 5,
                            ease: "none"
                          });
                        }
                      }
                    }}
                    onMouseEnter={() => {
                      gsap.to(`[data-dot="${index}"]`, {
                        scale: 1.2,
                        duration: 0.2,
                        ease: "power2.out"
                      });
                    }}
                    onMouseLeave={() => {
                      if (index !== currentMember) {
                        gsap.to(`[data-dot="${index}"]`, {
                          scale: 1,
                          duration: 0.2,
                          ease: "power2.out"
                        });
                      }
                    }}
                    data-dot={index}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Reduced Motion Fallback */}
        {prefersReducedMotion && (
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              Auto-scrolling is disabled based on your system preferences. Profiles will display statically.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default Team;